/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import {
  bexActionService,
  bexInsightsService,
  schemaService,
} from 'shared/api/services/BEXO';
import {
 useState, useEffect
} from 'react';
import {
 useParams
} from 'react-router-dom';

// Define types for the schema and row data
import {
 Column ,Row
} from './types/builderRecords.types';

interface Schema {
  columns: Column[];
}

interface RecordsData {
  columns: Array<{
    id: string;
    name: string;
    type: string;
  }>;
  rows: Row[];
}

interface Error {
  message: string;
}

export function useRecordsData(tableName: string) {
  const [recordsData, setRecordsData] = useState<RecordsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    async function fetchData() {
      try {
        const res = await schemaService.retrieveSchemas(
          tableName,
          appId,
          oragID
        );

        const schema: Schema | undefined = res?.data?.schemas?.[0];
        if (!isMounted) return;

        if (!schema || !schema.columns?.length) {
          throw new Error('Schema is missing or invalid');
        }

        setRecordsData({
          columns: schema.columns.map((col) => ({
            id: col.columnName,
            name: col.columnName,
            type: col.dataType,
          })),
          rows: [],
        });
      } catch (err) {
        console.error('Failed to fetch records', err);
        if (isMounted) setError({
 message: err.message
});
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    if (tableName) {
      fetchData();
    }

    return () => {
      isMounted = false;
    };
  }, [tableName, appId, oragID]);

  // Function to fetch schema
  const fetchSchema = async (): Promise<any> => {
    try {
      const response = await schemaService.retrieveSchemas(
        tableName,
        appId,
        oragID
      );

      return response;
    } catch (err) {
      console.error('Error fetching schema: ', err);
      setError({
 message: err.message
});
      throw err;
    }
  };

  // Function to fetch all records
  const fetchAllRecords = async (
    pageToRetrieve = 1
  ): Promise<{
    columns: Array<{
      id: string;
      name: string;
      type: string;
    }>;
    records: Row[];
    totalRecords: number;
  }> => {
    try {
      const payload = {
        target: tableName.toLowerCase(),
        action: 'search',
        filters: {
          logicalOperator: 'AND',
          rules: [
            {
              field: 'recordId',
              dataType: 'tag',
              comparisonOperator: 'notEquals',
              value: 'null',
            },
          ],
        },
        columnsToRetrieve: [],
        resultDisplayOrder: 'row',
        maxRowsPerPage: 10,
        pageToRetrieve,
      };

      const response = await bexInsightsService.searchV2(
        oragID,
        appId,
        payload
      );

      const rawRows = response?.tableRow || [];
      const totalRecords = response?.totalRecords || 0;

      // Dynamically determine column keys from the rows
      const columnKeys = Object.keys(
        rawRows.reduce(
          (acc, row) => ({
            ...acc,
            ...row,
          }),
          {}
        )
      );

      const columns = columnKeys.map((key) => ({
        id: key,
        name: key,
        type:
          typeof rawRows.find((r) => r[key] !== undefined)?.[key] === 'number'
            ? 'number'
            : 'text',
      }));

      // Normalize rows and make sure they each have a unique `id`
      const records = rawRows.map((row, i) => ({
        id: row.id || row.recordId || `row-${i}`,
        ...row,
      }));
      return {
        columns,
        records,
        totalRecords,
      };
    } catch (err) {
      console.error('Error fetching records: ', err);
      setError({
 message: err.message
});
      throw err;
    }
  };

  function formatLabel(input: string): string {
    return input
      .replace(/([A-Z])/g, ' $1')
      .replace(/^ /, '')
      .replace(/^./, (str) => str.toUpperCase())
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Function to create new records
  const addRecords = async (records: Row[]): Promise<any> => {
    try {
      const payload = {
        Meta: {
          Child: {
            Name: formatLabel(tableName),
            Type: 'Child Create',
          },
          Action: 'create',
        },
        ChildRecords: records.map((record) => {
          const childRecord: Row = {};

          recordsData?.columns.forEach((col) => {
            if (record[col.name] !== undefined && col.name !== 'recordId') {
              childRecord[col.name] = record[col.name]; // Dynamically map column names
            }
          });

          if (Object.keys(childRecord).length > 0) {
            childRecord.recordStatus = 'created'; // Set recordStatus to 'created'
          }

          return childRecord;
        }),
      };

      const response = await bexActionService.createRecords(
        oragID,
        appId,
        payload
      );

      if (response?.bulkCount?.success >= 1) {
        return response;
      } else {
        throw new Error('No records created successfully.');
      }
    } catch (err) {
      console.error('Error adding records: ', err);
      setError({
 message: err.message
});
      throw err;
    }
  };

  // Function to update existing records
  const modifyRecords = async (records: Row[]): Promise<any> => {

    try {
      const childRecords = records
        .map((record) => {
          if (!record.recordId) {
            console.warn(' Skipping record without recordId:', record);
            return null;
          }

          const childRecord: Row = {
            recordId: record.recordId,
            recordStatus: 'updated',
          };

          recordsData?.columns.forEach((col) => {
            const val = record[col.name];
            if (val !== undefined) {
              childRecord[col.name] = val;
            }
          });

          return childRecord;
        })
        .filter(Boolean); // remove nulls from missing recordId

      const payload = {
        Meta: {
          Child: {
            Name: tableName.charAt(0).toLowerCase() + tableName.slice(1),
            Type: 'Child Create',
          },
          Action: 'update',
        },
        ChildRecords: childRecords,
      };

      const response = await bexActionService.updateRecords(
        oragID,
        appId,
        payload
      );

      return response;
    } catch (err) {
      console.error(' Error updating records:', err);
      setError({
 message: err.message
});
      throw err;
    }
  };

  // Function to delete records
  const deleteRecords = async (records: Row[]): Promise<any> => {
    try {
      let payload;

      if (tableName === 'User') {
        const deletePromises = records.map((record) => {
          return bexActionService.deleteUser(oragID, appId, record.user_id);
        });

        const responses = await Promise.all(deletePromises);

        return responses;
      } else {
        payload = {
          Meta: {
            Child: {
              Name: tableName.charAt(0).toLowerCase() + tableName.slice(1),
              Type: 'Child Create',
            },
            Action: 'update',
          },
          ChildRecords: records.map((record) => {
            const childRecord: Row = {};

            recordsData?.columns.forEach((col) => {
              if (record[col.name] !== undefined) {
                childRecord[col.name] = record[col.name]; // Dynamically map column names
              }
            });

            if (Object.keys(childRecord).length > 0) {
              childRecord.recordStatus = 'deleted'; // Set recordStatus to 'deleted'
            }

            return childRecord;
          }),
        };

        const response = await bexActionService.deleteRecords(
          oragID,
          appId,
          payload
        );

        return response;
      }
    } catch (err) {
      console.error('Error deleting records: ', err);
      setError({
 message: err.message
});
      throw err;
    }
  };

  return {
    recordsData,
    setLoading,
    loading,
    error,
    addRecords,
    modifyRecords,
    deleteRecords,
    fetchAllRecords,
    fetchSchema,
  };
}
